import { useEffect, useRef, useState } from 'react';
import { EAssetType, EMaterialMapType, I3dModel, IFilesApiColor, IFilesApiMaterial, IFilesApiObject, IManufacturer, IMaterial, TTexture } from '../@types/FilesAPIModels';
import { IAttribute } from '../models/standard/AttributeModel';
import FilesAPI2DModel from '../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../models/standard/FilesAPIProductModel';
import { usePlan } from '../provider/PlanProvider';
import { mergeAttributesById } from '../utils/attributes/attributeCreationUtils';
import { API_FETCH_DEFAULT_BATCH_SIZE } from '../utils/constants';
import filesApiService, { FILES_API_ATTRIBUTE_CREATED_EVENT } from '../utils/filesApiService';
import {
  parseFilesApiBatchColorsRequest,
  parseFilesApiBatchMaterialsRequest,
  parseFilesApiManufacturersRequest,
  parseFilesApiMaterialRequest,
  parseFilesApiModelRequest,
  parseFilesApiSingleProductRequest
} from '../utils/jsonParser';
import { filterModels2D, filterProducts } from '../utils/searchUtils';

const useFilesApi = () => {
  const [products, setProducts] = useState<FilesAPIProductModel[]>([]);
  const [textureProducts, setTextureProducts] = useState<FilesAPIProductModel[]>([]);
  const [manufacturers, setManufacturers] = useState<IManufacturer[]>([]);
  const [attributes, setAttributes] = useState<IAttribute[]>([]);
  const [categories, setCategories] = useState<IFilesApiObject[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [loadTarget, setLoadTarget] = useState<number>(-1);
  const [textureLoadProgress, setTextureLoadProgress] = useState<number>(0);
  const [textureLoadTarget, setTextureLoadTarget] = useState<number>(0);
  const [textures, setTextures] = useState<TTexture[]>([]);
  const [textureFetchCompleted, setTextureFetchCompleted] = useState<boolean>(false);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState<boolean>(false);
  const [models3D, setModels3D] = useState<I3dModel[]>([]);
  const [models2D, setModels2D] = useState<FilesAPI2DModel[]>([]);
  const [isFetchingProductsAndModels, setIsFetchingProductsAndModels] = useState<boolean>(false);
  const [isFetchingTextures, setIsFetchingTextures] = useState<boolean>(false);

  function isFulfilled<T>(p: PromiseSettledResult<T>): p is PromiseFulfilledResult<T> {
    return p.status === 'fulfilled';
  }
  const { planModel } = usePlan(); // Access Plan Model to get Region
  const currentRegion = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const previousRegionRef = useRef<string | null>(null);
  const attributesFetchPromiseRef = useRef<Promise<void> | null>(null);
  const categoriesFetchPromiseRef = useRef<Promise<void> | null>(null);

  // Function to fetch data for a single product.
  // This function is fine as it's meant to be a single request handler.
  const handleApiDataForSingleProduct = async (idToFetch: number) => {
    let message = `An unknown error occurred while fetching product with id = ${idToFetch}!`;
    try {
      const singleApiResponse = await filesApiService.dataRequest(`product/${idToFetch}`);
      const tmpProduct = parseFilesApiSingleProductRequest(singleApiResponse);
      return tmpProduct;
    } catch (error) {
      if (error instanceof Error) {
        message = error.message;
      }
      setError(message);
      // Re-throw to allow Promise.allSettled to catch this specific failure
      throw new Error(message);
    }
  };

  useEffect(() => {
    const checkForAuthentification = async (): Promise<boolean> => {
      if (isAuthenticated) return Promise.resolve(true);
      try {
        await filesApiService.ensureAuthenticated();
        setIsAuthenticated(true);
        return Promise.resolve(true);
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while authenticating the user in the useFilesApi hook! ErrorMessage:\n' + message);
        return Promise.resolve(false);
      }
    };
    if (!isAuthenticated) {
      checkForAuthentification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // main fetching function for products and models.
  const fetchAllProductsAndModels = async () => {
    if (isFetchingProductsAndModels) return;
    setIsFetchingProductsAndModels(true);
    setProducts([]);
    setModels2D([]);
    setModels3D([]);
    setLoadProgress(0);

    try {
      const [productApiResponse, modelApiResponse] = await Promise.all([
        filesApiService.dataWithParamsRequest('product', { page: '0', size: '10000' }),
        filesApiService.dataWithParamsRequest('model', { page: '0', size: '10000' }),
      ]);

      const productCount = productApiResponse.length;
      const modelCount = modelApiResponse.length;
      setLoadTarget(productCount + modelCount);

      const fetchedProducts: FilesAPIProductModel[] = [];
      const fetchedModels2D: FilesAPI2DModel[] = [];
      const fetchedModels3D: I3dModel[] = [];
      const fetchedTextureProducts: FilesAPIProductModel[] = [];

      // Concurrent fetching for all products
      const productPromises = productApiResponse.map(async (productRaw: any) => {
        try {
          const tmpProduct = await handleApiDataForSingleProduct(productRaw.id);
          const returnProduct = new FilesAPIProductModel(
            tmpProduct.id,
            tmpProduct.ownerId,
            tmpProduct.publicFlag,
            tmpProduct.productId,
            tmpProduct.name,
            tmpProduct.description,
            tmpProduct.manufacturer,
            tmpProduct.isFavorite,
            tmpProduct.links,
            tmpProduct.attributes,
            tmpProduct.assets,
            tmpProduct.objects,
            tmpProduct.data,
            tmpProduct.previewImagePath,
            tmpProduct.previewPicture,
            tmpProduct.materialAssets,
            tmpProduct.modelAssets,
            tmpProduct.depth,
            tmpProduct.length,
            tmpProduct.country,
          );

          // Concurrently fetch material and model assets for the current product
          const materialAssetPromises = tmpProduct.assets
            .filter((asset) => asset.assetType === EAssetType.MATERIAL)
            .map((asset) => filesApiService.dataRequest(`product/${tmpProduct.id}/material/${asset.id}`));

          const modelAssetPromises = tmpProduct.assets
            .filter((asset) => asset.assetType === EAssetType.MODEL)
            .map((asset) => filesApiService.dataRequest(`product/${tmpProduct.id}/model/${asset.id}`));

          const [materialAssetResponses, modelAssetResponses] = await Promise.all([Promise.allSettled(materialAssetPromises), Promise.allSettled(modelAssetPromises)]);

          returnProduct.materialAssets = materialAssetResponses
            .filter((result) => result.status === 'fulfilled')
            .map((result: PromiseSettledResult<any>) => parseFilesApiMaterialRequest((result as PromiseFulfilledResult<any>).value));

          returnProduct.modelAssets = modelAssetResponses
            .filter((result) => result.status === 'fulfilled')
            .map((result: PromiseSettledResult<any>) => parseFilesApiModelRequest((result as PromiseFulfilledResult<any>).value));

          // Extract preview image path concurrently for products
          returnProduct.previewImagePath = await extractPreviewImagePath(returnProduct);

          returnProduct.findDimensions();

          if (returnProduct.modelAssets?.some((asset) => asset.file2d)) {
            fetchedProducts.push(returnProduct);
          }

          const hasColorAsset = returnProduct.assets?.some((asset) => asset.assetType === EAssetType.COLOR);
          let hasValidMaterialAsset = false;

          // Check for valid material asset concurrently
          if (returnProduct.materialAssets && returnProduct.materialAssets.length > 0) {
            hasValidMaterialAsset = await productMaterialHasValidMap(tmpProduct.id, returnProduct.materialAssets);
          }

          if (hasColorAsset || hasValidMaterialAsset) {
            fetchedTextureProducts.push(returnProduct);
          }
          setLoadProgress((prev) => prev + 1); // Update progress for each product after processing
          return returnProduct; // Return the product for aggregation if needed
        } catch (productError) {
          console.error(`Error processing product with ID ${productRaw.id}:`, productError);
          setLoadProgress((prev) => prev + 1); // Still update progress even if an error occurs
          return null; // Return null to indicate failure for this product
        }
      });

      // Execute all product promises concurrently
      await Promise.allSettled(productPromises); // Use allSettled to ensure all promises run to completion

      setProducts(fetchedProducts);
      setTextureProducts(fetchedTextureProducts);

      // Concurrent fetching for all models
      const modelPromises = modelApiResponse.map(async (modelRaw: any) => {
        try {
          const modelData = await filesApiService.dataRequest(`model/${modelRaw.id}`);
          const parsedModel = parseFilesApiModelRequest(modelData);

          // Concurrently fetch preview image or SVG for models
          let previewImagePath = '';
          const previewImagePromise = filesApiService.modelPreviewImageRequest(parsedModel);
          const svgFilePromise = parsedModel.file2d ? filesApiService.getSVGFileFromModel(parsedModel.id) : Promise.resolve('');

          const [previewImageResult, svgFileResult] = await Promise.allSettled([previewImagePromise, svgFilePromise]);
          if (previewImageResult.status === 'fulfilled' && previewImageResult.value) {
            previewImagePath = previewImageResult.value;
          } else if (svgFileResult.status === 'fulfilled' && svgFileResult.value) {
            previewImagePath = svgFileResult.value;
          }

          parsedModel.previewImagePath = previewImagePath;
          fetchedModels3D.push(parsedModel);

          if (parsedModel.file2d) {
            const file2d = parsedModel.file2d;
            const model2D = new FilesAPI2DModel(
              parsedModel.id,
              file2d.ownerId,
              parsedModel.name,
              file2d.filePath,
              file2d.fileName,
              file2d.fileFormat,
              file2d.fileSize,
              parsedModel.depth,
              parsedModel.length,
              parsedModel.previewImagePath,
              parsedModel.object,
              parsedModel.rotation2D ?? 0,
            );
            fetchedModels2D.push(model2D);
          }
          setLoadProgress((prev) => prev + 1); // Update progress for each model after processing
          return parsedModel;
        } catch (modelError) {
          console.error(`Error processing model with ID ${modelRaw.id}:`, modelError);
          setLoadProgress((prev) => prev + 1); // Still update progress even if an error occurs
          return null; // Return null to indicate failure for this model
        }
      });

      // Execute all model promises concurrently
      await Promise.allSettled(modelPromises); // Use allSettled to ensure all promises run to completion

      setModels3D(fetchedModels3D);
      setModels2D(fetchedModels2D);
    } catch (e) {
      let message = 'An unknown error occurred!';
      if (e instanceof Error) message = e.message;
      setError('Error occurred while fetching data in the useFilesApi hook:\n' + message);
    } finally {
      setIsFetchingProductsAndModels(false);
      setHasFetchedInitialData(true); // marking initial fetch as complete
    }
  };

  // Fetch textures.
  const fetchTexturesData = async (): Promise<void> => {
    if (isFetchingTextures) return;
    setIsFetchingTextures(true);
    setTextures([]);
    setTextureLoadProgress(0);
    setTextureLoadTarget(0);

    try {
      // Concurrently fetch materials and colors
      const [materialsResult, colorsResult] = await Promise.allSettled([fetchMaterials(), fetchColors()]);

      const tmpTextures: TTexture[] = [];
      if (colorsResult.status === 'fulfilled') {
        tmpTextures.push(...colorsResult.value);
      } else {
        console.error('Failed to fetch colors:', colorsResult.reason);
        setError(`Error fetching colors: ${colorsResult.reason}`);
      }

      if (materialsResult.status === 'fulfilled') {
        tmpTextures.push(...materialsResult.value);
      } else {
        console.error('Failed to fetch materials:', materialsResult.reason);
        setError((prev) => (prev ? `${prev}\nError fetching materials: ${materialsResult.reason}` : `Error fetching materials: ${materialsResult.reason}`));
      }

      setTextures(tmpTextures);
      setTextureFetchCompleted(true);
    } catch (e) {
      let message = 'An unknown error occurred!';
      if (e instanceof Error) {
        message = e.message;
      }
      setError('Error occurred while fetching textures from the api! ErrorMessage:\n' + message);
    } finally {
      setIsFetchingTextures(false);
    }
  };

  // Exposed refetch functions
  const refetchAllProductsAndModels = async () => {
    if (!isFetchingProductsAndModels) {
      // Concurrently fetch products/models, manufacturers, attributes, and categories
      await Promise.allSettled([fetchAllProductsAndModels(), fetchManufacturers(), fetchAttributes(), fetchCategories()]);
    }
  };

  const ensureAllProductsAndModels = async () => {
    if (isFetchingProductsAndModels) return;

    const requests: Array<Promise<void>> = [fetchManufacturers(), fetchAttributes(), fetchCategories()];
    if (!hasFetchedInitialData) {
      requests.push(fetchAllProductsAndModels());
    }

    await Promise.allSettled(requests);
  };

  const refetchTextures = async () => {
    if (!isFetchingTextures) {
      await Promise.allSettled([fetchTexturesData(), fetchAllProductsAndModels()]);
    }
  };

  const ensureTextures = async () => {
    if (isFetchingTextures) return;

    const requests: Array<Promise<void>> = [];
    if (!textureFetchCompleted) {
      requests.push(fetchTexturesData());
    }
    if (!hasFetchedInitialData) {
      requests.push(fetchAllProductsAndModels());
    }

    await Promise.allSettled(requests);
  };
  // Reset initial fetch flag if region changes, so it refetches everything with new region params
  useEffect(() => {
    // Only trigger if we are authenticated AND the region has actually changed
    if (isAuthenticated && currentRegion !== previousRegionRef.current) {
      setAttributes([]);
      setCategories([]);
      setHasFetchedInitialData(false);
      previousRegionRef.current = currentRegion;
    }
  }, [isAuthenticated, currentRegion]);

  useEffect(() => {
    if (isAuthenticated && !hasFetchedInitialData) {
      void Promise.allSettled([fetchAttributes(), fetchCategories()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasFetchedInitialData]);

  // Determine overall isLoading
  useEffect(() => {
    setIsLoading(isFetchingProductsAndModels || isFetchingTextures);
  }, [isFetchingProductsAndModels, isFetchingTextures]);

  // Utility to extract a preview image path from a product.
  // This function is still sequential as it needs to try options in order.
  // The optimization for it will come from the concurrent product processing.
  const extractPreviewImagePath = async (product: FilesAPIProductModel): Promise<string> => {
    try {
      // Prioritize direct previewPicture
      if (product.previewPicture) {
        const url = await filesApiService.previewImageRequest(product.id, 'thumb');
        if (url) return url;
      }

      // Try material assets
      if (product.materialAssets?.length > 0) {
        for (const materialAsset of product.materialAssets) {
          if (materialAsset.maps?.length > 0) {
            for (const map of materialAsset.maps) {
              if (map.type === EMaterialMapType.map) {
                const url = await filesApiService.materialPreviewPictureRequest(materialAsset.id, 'thumb');
                if (url) return url;
              }
            }
          }
        }
      }

      // Try model assets
      if (product.modelAssets?.length > 0) {
        for (const modelAsset of product.modelAssets) {
          if (modelAsset.previewPicture) {
            const url = await filesApiService.productPreviewImageRequest(product, 'thumb'); // This will call the model preview internally
            if (url) return url;
          } else if (modelAsset.file2d) {
            const url = await filesApiService.getSVGFileFromProduct(product.id, modelAsset.id);
            if (url) return url;
          }
        }
      }

      // Try product data files
      if (product.data?.length > 0) {
        for (const dataFile of product.data) {
          if (dataFile.filePath) {
            const url = await filesApiService.productPreviewImageRequest(product, 'thumb'); // This might try to find a generic file preview
            if (url) return url;
          }
        }
      }

      return ''; // No preview image found
    } catch (e) {
      let message = 'An unknown error occurred!';
      if (e instanceof Error) {
        message = e.message;
      }
      setError('Error occurred while extracting preview image path in the useFilesApi hook! ErrorMessage:\n' + message);
      return '';
    }
  };

  // Fetch manufacturers.
  const fetchManufacturers = async () => {
    if (isAuthenticated && manufacturers.length === 0) {
      try {
        const manufacturersApiResponse = await filesApiService.dataRequest('manufacturer/all');
        const tmpManufacturers = parseFilesApiManufacturersRequest(manufacturersApiResponse);
        setManufacturers([...tmpManufacturers]);
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while parsing a manufacturer object in the drawer! ErrorMessage:\n' + message);
      }
    }
  };

  // Fetch attributes.
  const fetchAttributes = async () => {
    if (!isAuthenticated || attributes.length !== 0) return;
    if (attributesFetchPromiseRef.current) return attributesFetchPromiseRef.current;

    attributesFetchPromiseRef.current = (async () => {
      try {
        const fetchedAttributes = await filesApiService.getAttributes(currentRegion);
        setAttributes(fetchedAttributes);
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while parsing an attribute object in the drawer! ErrorMessage:\n' + message);
      }
    })().finally(() => {
      attributesFetchPromiseRef.current = null;
    });

    return attributesFetchPromiseRef.current;
  };

  useEffect(() => {
    const handleAttributeCreated = (event: Event) => {
      const createdAttribute = (event as CustomEvent<IAttribute>).detail;
      if (!createdAttribute) return;

      setAttributes((prev) => mergeAttributesById(prev, [createdAttribute]));
      filesApiService
        .getAttributes(currentRegion)
        .then((refreshedAttributes) => {
          setAttributes((prev) => mergeAttributesById(refreshedAttributes, prev, [createdAttribute]));
        })
        .catch(() => undefined);
    };

    window.addEventListener(FILES_API_ATTRIBUTE_CREATED_EVENT, handleAttributeCreated);
    return () => window.removeEventListener(FILES_API_ATTRIBUTE_CREATED_EVENT, handleAttributeCreated);
  }, [currentRegion]);

  // Fetch categories.
  const fetchCategories = async () => {
    if (!isAuthenticated || categories.length !== 0) return;
    if (categoriesFetchPromiseRef.current) return categoriesFetchPromiseRef.current;

    categoriesFetchPromiseRef.current = (async () => {
      try {
        const fetchedObjects = await filesApiService.getObjects(currentRegion);
        setCategories(fetchedObjects);
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while parsing a product category object in the drawer! ErrorMessage:\n' + message);
      }
    })().finally(() => {
      categoriesFetchPromiseRef.current = null;
    });

    return categoriesFetchPromiseRef.current;
  };

  // Fetch colors.
  const fetchColors = async (): Promise<IFilesApiColor[]> => {
    if (isAuthenticated) {
      try {
        const colors: IFilesApiColor[] = [];
        const initialColorResponse = await filesApiService.dataRequest(`color?page=0&size=${API_FETCH_DEFAULT_BATCH_SIZE}`);
        const initialBatch = parseFilesApiBatchColorsRequest(initialColorResponse.content);
        colors.push(...initialBatch);

        const totalPages = initialColorResponse.totalPages;
        setTextureLoadTarget((prev) => prev + initialColorResponse.totalElements); // Set total elements for progress
        setTextureLoadProgress((prev) => prev + initialBatch.length);

        const subsequentPagePromises = [];
        for (let i = 1; i < totalPages; i++) {
          subsequentPagePromises.push(
            filesApiService.dataRequest(`color?page=${i}&size=${API_FETCH_DEFAULT_BATCH_SIZE}`).then((response) => {
              const batch = parseFilesApiBatchColorsRequest(response.content);
              setTextureLoadProgress((prev) => prev + batch.length);
              return batch;
            }),
          );
        }

        const subsequentBatches = await Promise.all(subsequentPagePromises);
        subsequentBatches.forEach((batch) => colors.push(...batch));

        return colors;
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while fetching colors from the api! ErrorMessage:\n' + message);
        return [];
      }
    }
    return [];
  };

  // Fetch material preview pictures.
  // This will also be called concurrently.
  const fetchMaterialPreviewPictures = async (materials: IFilesApiMaterial[]): Promise<IFilesApiMaterial[]> => {
    if (isAuthenticated) {
      try {
        const materialPreviewPromises = materials.map(async (material) => {
          const previewImageUrl = await filesApiService.materialPreviewPictureRequest(material.id, 'high');
          const tmpMaterial = { ...material };
          tmpMaterial.previewPictureUrl = previewImageUrl;
          return tmpMaterial;
        });

        const results = await Promise.allSettled(materialPreviewPromises);
        const successfulMaterials = results.filter(isFulfilled).map((result) => result.value);
        return successfulMaterials;
      } catch (error) {
        let message = 'An unknown error occurred while fetching a material preview image!';
        if (error instanceof Error) {
          message = error.message;
        }
        setError(message);
        return [];
      }
    }
    return [];
  };

  /**
   * Returns true if at least one of the given materialAssets
   * for the given product has a map of type `EMaterialMapType.map`.
   */
  const productMaterialHasValidMap = async (productId: number, materialAssets: { id: number }[]): Promise<boolean> => {
    if (!materialAssets?.length) {
      return false;
    }

    // fire off one getProductMaterial per asset
    const settles = await Promise.allSettled(materialAssets.map((asset) => filesApiService.getProductMaterial(productId, asset.id)));

    // look for the first fulfilled one whose value has a map of the right type
    for (const r of settles) {
      if (r.status === 'fulfilled') {
        const mat = r.value as IMaterial | undefined;
        if (mat && Array.isArray(mat.maps) && mat.maps.some((m) => m.type === EMaterialMapType.map)) {
          return true;
        }
      }
    }

    return false;
  };

  // checks if material has a valid map right now, since we are only loading maps in our plan
  const materialHasValidMap = async (materialAssets: any[]): Promise<boolean> => {
    // Concurrently fetch detailed material for all material assets
    const detailedMaterialPromises = materialAssets.map((asset) => filesApiService.getMaterial(asset.id));
    const results = await Promise.allSettled(detailedMaterialPromises);

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        if (result.value.maps.some((map) => map.type === EMaterialMapType.map)) {
          return true;
        }
      }
    }
    return false;
  };

  // Fetch materials.
  const fetchMaterials = async (): Promise<IFilesApiMaterial[]> => {
    if (isAuthenticated) {
      try {
        const materialApiResponse = await filesApiService.dataWithParamsRequest('material', { page: '0', size: '10000' });
        const materials: IFilesApiMaterial[] = parseFilesApiBatchMaterialsRequest(materialApiResponse);

        setTextureLoadTarget((prev) => prev + materials.length); // Set total materials for progress

        const filteredMaterialsPromises = materials.map(async (material) => {
          const detailedMaterial: IMaterial | undefined = await filesApiService.getMaterial(material.id);
          if (detailedMaterial && detailedMaterial.maps.some((map) => map.type === EMaterialMapType.map)) {
            setTextureLoadProgress((prev) => prev + 1); // Update progress for each material that has a valid map
            return material;
          }
          setTextureLoadProgress((prev) => prev + 1); // Still update progress even if it doesn't have a valid map
          return null; // Return null if not valid
        });

        const filteredMaterialResults = await Promise.allSettled(filteredMaterialsPromises);
        const validAndFulfilledResults = filteredMaterialResults.filter(isFulfilled);

        const filteredMaterials = validAndFulfilledResults.filter((result) => result.value !== null).map((result) => result.value as IFilesApiMaterial);

        return await fetchMaterialPreviewPictures(filteredMaterials);
      } catch (e) {
        let message = 'An unknown error occurred!';
        if (e instanceof Error) {
          message = e.message;
        }
        setError('Error occurred while fetching materials from the api! ErrorMessage:\n' + message);
        return [];
      }
    }
    return [];
  };

  // Sanitize and return values.
  const sanitizedProducts = filterProducts(products);
  const sanitized2DModels = filterModels2D(models2D);

  return {
    products: sanitizedProducts,
    models3D,
    models2D: sanitized2DModels,
    isAuthenticated,
    isLoading,
    loadProgress,
    loadTarget,
    textureLoadProgress,
    textureLoadTarget,
    error,
    categories,
    attributes,
    manufacturers,
    textureProducts,
    textures,
    textureFetchCompleted,
    ensureAllProductsAndModels,
    ensureTextures,
    refetchAllProductsAndModels,
    refetchTextures,
    isFetchingProductsAndModels,
    isFetchingTextures,
  };
};

export default useFilesApi;
